import * as THREE from "three";
import { SYRUPS, type Syrup } from "../game/coffee";
import type { DishKind } from "../game/dishes";
import { COFFEE_TRUCK, COUNTER_TOP, slotCentre, type Box, type Station } from "../game/kitchen";
import { SKY, VIEW_FAR, flat, mergeStatic, type Outside } from "./boardwalk";
import { box, paint, span, steel, type Room } from "./build";
import type { RoomLook } from "./looks";
import { menuBoard, skyGradient } from "./textures";

// The Daily Grind, built from `kitchen.ts`'s `COFFEE_TRUCK` layout: a narrow
// galley inside a food truck. Along the back counter the milk fridge, the
// espresso machine with its two group heads and steam wand, the grinder,
// three syrup bottles with pumps, the hand sink, the bin of dirty glassware
// and the clean shelf; across the aisle the paper cups, the bar and the
// service window onto the food truck lot (`buildLot`), and the trash.

const L = COFFEE_TRUCK;
const S = L.stations as Record<Station, Box>;
const { width: W, depth: D, height: H } = L;
/** The bar's wooden top, a few centimetres proud of the steel counter. */
const BAR_TOP = COUNTER_TOP + 0.03;

const SYRUP_COLORS: Record<Syrup, number> = { vanilla: 0xf1e3b8, caramel: 0xc7832e, hazelnut: 0x7a4a26 };

export function buildTruck(restaurantName: string, look: RoomLook): Room {
  const g = new THREE.Group();
  const highlight: Partial<Record<Station, THREE.MeshStandardMaterial[]>> = {};
  const lit = (station: Station, m: THREE.MeshStandardMaterial) => {
    (highlight[station] ??= []).push(m);
    return m;
  };

  // --- the shell: a rubber-matted floor, painted steel walls, a light strip ---------
  const floor = new THREE.Mesh(new THREE.PlaneGeometry(W, D), paint(0x34383c, 0.95));
  floor.rotation.x = -Math.PI / 2;
  floor.position.set(W / 2, 0, D / 2);
  floor.receiveShadow = true;
  g.add(floor);
  const wallMat = paint(0xe9e3d3, 0.7);
  const back = new THREE.Mesh(new THREE.PlaneGeometry(W, H), wallMat);
  back.position.set(W / 2, H / 2, 0);
  g.add(back);
  for (const [x, ry] of [
    [0, Math.PI / 2],
    [W, -Math.PI / 2],
  ] as const) {
    const side = new THREE.Mesh(new THREE.PlaneGeometry(D, H), wallMat);
    side.rotation.y = ry;
    side.position.set(x, H / 2, D / 2);
    g.add(side);
  }
  const { x0, x1, y0, y1 } = L.opening;
  for (const [a, b, c, d] of [
    [0, x0, 0, H],
    [x1, W, 0, H],
    [x0, x1, 0, y0],
    [x0, x1, y1, H],
  ] as const) {
    const m = new THREE.Mesh(new THREE.PlaneGeometry(b - a, d - c), wallMat);
    m.rotation.y = Math.PI;
    m.position.set((a + b) / 2, (c + d) / 2, D);
    g.add(m);
  }
  const ceiling = new THREE.Mesh(new THREE.PlaneGeometry(W, D), paint(0xf4f1ea));
  ceiling.rotation.x = Math.PI / 2;
  ceiling.position.set(W / 2, H, D / 2);
  g.add(ceiling);
  span(0.4, W - 0.4, H - 0.04, H, D / 2 - 0.08, D / 2 + 0.08, new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: 0xfff3dc, emissiveIntensity: 1.2 }), g);

  // --- the back counter ---------------------------------------------------------------
  const counter = steel();
  span(0, W, 0, COUNTER_TOP - 0.02, 0, 0.68, counter, g);
  span(0, W, COUNTER_TOP - 0.02, COUNTER_TOP, 0, 0.68, steel(), g);
  span(0, W, COUNTER_TOP, 1.25, 0, 0.02, steel(), g); // backsplash

  // The milk fridge, under the counter: a glass-fronted door with milk inside.
  const mk = S.milk;
  span(mk.min[0], mk.max[0], 0.05, mk.max[1] - 0.04, mk.max[2], mk.max[2] + 0.01, lit("milk", paint(0x9fb7c4, 0.2)), g);
  span(mk.max[0] - 0.08, mk.max[0] - 0.05, 0.35, 0.75, mk.max[2] + 0.01, mk.max[2] + 0.04, paint(0x2b2d42, 0.4), g);
  for (let i = 0; i < 4; i++) box(0.07, 0.2, 0.07, paint(0xf7f7f2, 0.5), mk.min[0] + 0.12 + i * 0.13, 0.2, mk.max[2] - 0.1, g);
  label(g, "MILK", (mk.min[0] + mk.max[0]) / 2, 0.8, mk.max[2] + 0.012);

  // The espresso machine: a red body, two group heads, a drip tray, the steam wand.
  const gp = S.group;
  const wd = S.wand;
  const machine = lit("group", paint(0xb3261e, 0.35));
  span(gp.min[0] - 0.05, wd.max[0] + 0.03, COUNTER_TOP, gp.min[1] + 0.02, 0.05, 0.42, machine, g); // base
  span(gp.min[0] - 0.05, wd.max[0] + 0.03, 1.36, 1.62, 0.05, 0.42, machine, g); // top
  span(gp.min[0] - 0.05, gp.min[0] - 0.02, gp.min[1], 1.36, 0.05, 0.42, steel(), g);
  span(wd.max[0], wd.max[0] + 0.03, gp.min[1], 1.36, 0.05, 0.42, steel(), g);
  span(gp.min[0], wd.max[0], gp.min[1] + 0.01, gp.min[1] + 0.025, 0.22, 0.6, lit("group", paint(0x2b2d42, 0.4)), g); // drip tray
  span(gp.min[0], wd.max[0], 1.3, 1.36, 0.05, 0.14, steel(), g); // back panel band
  const groupHeads = [0, 1].map((i) => new THREE.Vector3(...slotCentre(gp, i, 2)));
  for (const at of groupHeads) {
    const head = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.04, 0.06, 20), lit("group", steel()));
    head.position.set(at.x, 1.3, 0.32);
    g.add(head);
  }
  const wandAt = new THREE.Vector3((wd.min[0] + wd.max[0]) / 2, 0, 0.32);
  const wandPipe = new THREE.Mesh(new THREE.CylinderGeometry(0.006, 0.006, 0.3, 8), lit("wand", steel()));
  wandPipe.position.set(wandAt.x, 1.2, wandAt.z);
  wandPipe.rotation.z = 0.12;
  g.add(wandPipe);
  const wandKnob = new THREE.Mesh(new THREE.CylinderGeometry(0.018, 0.018, 0.03, 12), paint(0x1d1d1f, 0.5));
  wandKnob.rotation.x = Math.PI / 2;
  wandKnob.position.set(wandAt.x, 1.48, 0.43);
  g.add(wandKnob);
  // A pressure gauge on the front.
  const gauge = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.01, 20), paint(0xf6f4ee, 0.4));
  gauge.rotation.x = Math.PI / 2;
  gauge.position.set((gp.min[0] + wd.max[0]) / 2, 1.5, 0.425);
  g.add(gauge);

  // The grinder: a hopper of beans on a black body.
  const gr = S.grinder;
  const grinderMat = lit("grinder", paint(0x1f2124, 0.45));
  span(gr.min[0] + 0.04, gr.max[0] - 0.04, COUNTER_TOP, 1.22, gr.min[2] + 0.05, gr.max[2] - 0.05, grinderMat, g);
  const hopper = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.06, 0.26, 16), new THREE.MeshStandardMaterial({ color: 0xd9e4ea, transparent: true, opacity: 0.45, roughness: 0.1 }));
  hopper.position.set((gr.min[0] + gr.max[0]) / 2, 1.35, (gr.min[2] + gr.max[2]) / 2);
  g.add(hopper);
  const beans = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.06, 0.16, 16), paint(0x3b2213, 0.9));
  beans.position.set(hopper.position.x, 1.3, hopper.position.z);
  g.add(beans);
  span(gr.min[0] + 0.1, gr.max[0] - 0.1, 1.02, 1.04, gr.max[2] - 0.08, gr.max[2], steel(), g); // the fork the portafilter rests in

  // The syrup pumps: a bottle per syrup, with its name.
  for (const sy of SYRUPS) {
    const b = S[`pump:${sy}`];
    const cx = (b.min[0] + b.max[0]) / 2;
    const cz = (b.min[2] + b.max[2]) / 2;
    const bottle = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 0.24, 14), lit(`pump:${sy}`, new THREE.MeshStandardMaterial({ color: SYRUP_COLORS[sy], roughness: 0.25, transparent: true, opacity: 0.9 })));
    bottle.position.set(cx, COUNTER_TOP + 0.12, cz);
    g.add(bottle);
    const pump = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.1, 8), paint(0x1d1d1f, 0.5));
    pump.position.set(cx, COUNTER_TOP + 0.29, cz);
    g.add(pump);
    span(cx - 0.005, cx + 0.05, COUNTER_TOP + 0.33, COUNTER_TOP + 0.345, cz - 0.01, cz + 0.01, paint(0x1d1d1f, 0.5), g);
    label(g, sy.toUpperCase(), cx, COUNTER_TOP + 0.06, b.max[2] + 0.04, 0.2);
  }

  // The hand sink.
  const sk = S.sink;
  const sinkMat = lit("sink", steel());
  const [bx0, bx1, bz0, bz1, bb] = [sk.min[0] + 0.05, sk.max[0] - 0.05, sk.min[2] + 0.05, sk.max[2] - 0.08, 0.66];
  span(bx0, bx1, bb, bb + 0.01, bz0, bz1, sinkMat, g);
  for (const [a, b2, c, d] of [
    [bx0, bx0 + 0.01, bz0, bz1],
    [bx1 - 0.01, bx1, bz0, bz1],
    [bx0, bx1, bz0, bz0 + 0.01],
    [bx0, bx1, bz1 - 0.01, bz1],
  ] as const) span(a, b2, bb, COUNTER_TOP, c, d, sinkMat, g);
  const water = new THREE.Mesh(new THREE.PlaneGeometry(bx1 - bx0 - 0.02, bz1 - bz0 - 0.02), new THREE.MeshStandardMaterial({ color: 0x9fd3e6, transparent: true, opacity: 0.55, roughness: 0.1 }));
  water.rotation.x = -Math.PI / 2;
  water.position.set((bx0 + bx1) / 2, bb + 0.1, (bz0 + bz1) / 2);
  g.add(water);
  box(0.025, 0.22, 0.025, steel(), (bx0 + bx1) / 2, COUNTER_TOP + 0.11, 0.05, g);
  box(0.025, 0.025, 0.16, steel(), (bx0 + bx1) / 2, COUNTER_TOP + 0.22, 0.13, g);

  // The bin of dirty glassware, and the clean shelf.
  const tb = S.tub;
  const binMat = lit("tub", paint(0x6f7a84, 0.55));
  span(tb.min[0], tb.max[0], COUNTER_TOP, COUNTER_TOP + 0.01, tb.min[2], tb.max[2], binMat, g);
  for (const [a, b2, c, d] of [
    [tb.min[0], tb.min[0] + 0.012, tb.min[2], tb.max[2]],
    [tb.max[0] - 0.012, tb.max[0], tb.min[2], tb.max[2]],
    [tb.min[0], tb.max[0], tb.min[2], tb.min[2] + 0.012],
    [tb.min[0], tb.max[0], tb.max[2] - 0.012, tb.max[2]],
  ] as const) span(a, b2, COUNTER_TOP, tb.max[1], c, d, binMat, g);
  label(g, "DIRTY", (tb.min[0] + tb.max[0]) / 2, tb.max[1] - 0.06, tb.max[2] + 0.002, 0.18);
  const sh = S.shelf;
  const wire = lit("shelf", new THREE.MeshStandardMaterial({ color: 0xd8dde2, metalness: 0.6, roughness: 0.4 }));
  const shelfLevels = [1.0, 1.32, 1.64];
  for (const y of shelfLevels) span(sh.min[0], sh.max[0], y - 0.012, y, sh.min[2], sh.max[2], wire, g);
  for (const x of [sh.min[0], sh.max[0]]) span(x - 0.008, x + 0.008, COUNTER_TOP, 1.75, sh.max[2] - 0.02, sh.max[2], wire, g);

  // The menu board over the sink and bin.
  const menu = new THREE.Mesh(new THREE.PlaneGeometry(0.9, 0.62), new THREE.MeshStandardMaterial({ map: menuBoard(restaurantName, look.specials), roughness: 0.8 }));
  menu.position.set(3.85, 1.75, 0.012);
  g.add(menu);

  // --- the front counter: cups, the bar, the window, the trash ------------------------
  span(0, x0, 0, COUNTER_TOP - 0.02, 1.83, D, steel(), g);
  span(x0, x1, 0, y0 - 0.05, 2.3, D, steel(), g);
  // The counter stops short of the right wall: the trash can stands in the gap.
  span(x1, 4.55, 0, COUNTER_TOP - 0.02, 1.83, D, steel(), g);
  span(0, x0, COUNTER_TOP - 0.02, COUNTER_TOP, 1.83, D, steel(), g);
  span(x1, 4.55, COUNTER_TOP - 0.02, COUNTER_TOP, 1.83, D, steel(), g);
  // A stack of paper cups, upside down.
  const cp = S.cups;
  const cupMat = lit("cups", paint(0xf6f2ea, 0.8));
  for (let i = 0; i < 2; i++) {
    const stack = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.042, 0.3, 18), cupMat);
    stack.position.set(cp.min[0] + 0.1 + i * 0.14, COUNTER_TOP + 0.15, (cp.min[2] + cp.max[2]) / 2);
    g.add(stack);
  }
  // The bar: a wooden top to make coffees on, sitting proud of the steel
  // (flush with it, the two surfaces fight over the same pixels and flicker).
  const br = S.bar;
  span(br.min[0], br.max[0], COUNTER_TOP, BAR_TOP, br.min[2], br.max[2], lit("bar", paint(0x9a6a3c, 0.6)), g);
  // The window's ledge, inside and out, and its frame.
  const ledge = lit("window", steel());
  span(x0, x1, y0 - 0.05, y0, 2.25, D + 0.3, ledge, g);
  for (const x of [x0, x1]) span(x - 0.012, x + 0.012, y0, y1, D - 0.02, D + 0.1, paint(0xe8e6de), g);
  span(x0, x1, y1 - 0.012, y1 + 0.012, D - 0.02, D + 0.1, paint(0xe8e6de), g);
  const ticketRail = new THREE.Vector3((x0 + x1) / 2 - 0.15, y1 - 0.04, D - 0.03);
  span(x0 + 0.05, x1 - 0.05, y1 - 0.03, y1 - 0.01, D - 0.04, D - 0.02, steel(), g);
  // An awning over the window, outside: striped.
  for (let i = 0; i < 9; i++) {
    const stripe = new THREE.Mesh(new THREE.BoxGeometry((x1 - x0 + 0.6) / 9, 0.02, 0.9), flat(i % 2 ? 0xf6f2ea : 0x2f6f6a));
    stripe.position.set(x0 - 0.3 + ((i + 0.5) * (x1 - x0 + 0.6)) / 9, y1 + 0.32, D + 0.4);
    stripe.rotation.x = -0.35;
    g.add(stripe);
  }
  // The trash.
  const tr = S.trash;
  const can = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.2, tr.max[1], 18), lit("trash", paint(0x3d4a3f, 0.6)));
  can.position.set((tr.min[0] + tr.max[0]) / 2, tr.max[1] / 2, (tr.min[2] + tr.max[2]) / 2);
  g.add(can);

  // A wall clock on the left wall.
  const cx = 0.012;
  const cy = 1.85;
  const cz = 1.25;
  const face = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.13, 0.02, 32), paint(0xfdfdf8, 0.5));
  face.rotation.z = Math.PI / 2;
  face.position.set(cx, cy, cz);
  g.add(face);
  const hand = (len: number, w: number) => {
    const pivot = new THREE.Group();
    pivot.position.set(cx + 0.015, cy, cz);
    pivot.rotation.y = Math.PI / 2;
    const h = new THREE.Mesh(new THREE.BoxGeometry(w, len, 0.004), paint(0x2b2d42, 0.4));
    h.position.y = len / 2;
    pivot.add(h);
    g.add(pivot);
    return pivot;
  };
  const hourHand = hand(0.07, 0.012);
  const minuteHand = hand(0.1, 0.008);

  const outside = buildLot(D);
  g.add(outside.group);

  // There's no dish machine in a truck: these stand in for it, out of sight.
  const hood = new THREE.Group();
  const lamp = new THREE.MeshStandardMaterial({ color: 0x333333 });
  const offstage = new THREE.Vector3(W / 2, -5, D / 2);

  return {
    group: g,
    hood,
    lamp,
    machineBox: { min: [W / 2, -6, D / 2], max: [W / 2 + 0.1, -5.9, D / 2 + 0.1] },
    hourHand,
    minuteHand,
    highlight,
    anchors: {
      sink: new THREE.Vector3((bx0 + bx1) / 2, bb + 0.1, (bz0 + bz1) / 2),
      rack: offstage,
      machineRack: offstage,
      ticketRail,
    },
    shelfSpot(kind: DishKind, i: number) {
      // Shot glasses upside down on the top shelf, pitchers on the middle one.
      if (kind === "shotglass") return { pos: new THREE.Vector3(sh.min[0] + 0.06 + (i % 4) * 0.1, shelfLevels[2]! + 0.07, sh.min[2] + 0.12 + Math.floor(i / 4) * 0.15), flip: true, turn: 0 };
      return { pos: new THREE.Vector3(sh.min[0] + 0.08 + (i % 3) * 0.14, shelfLevels[Math.floor(i / 3) ? 0 : 1]!, sh.min[2] + 0.2), flip: false, turn: Math.PI };
    },
    outside,
    keyLight: { from: new THREE.Vector3(2.6, 2.2, 1.3), at: new THREE.Vector3(2.6, 0.3, 1.0) },
    passLight: new THREE.Vector3((x0 + x1) / 2, 1.5, D + 0.4),
    cafe: {
      groups: groupHeads.map((at) => new THREE.Vector3(at.x, COUNTER_TOP + 0.03, 0.32)),
      grouphead: groupHeads.map((at) => new THREE.Vector3(at.x, 1.25, 0.32)),
      wand: new THREE.Vector3(wandAt.x, COUNTER_TOP + 0.03, wandAt.z),
      bar: new THREE.Vector3((br.min[0] + br.max[0]) / 2, BAR_TOP, (br.min[2] + br.max[2]) / 2),
    },
  };
}

/** A small painted label on a station's face (facing +z). */
function label(parent: THREE.Object3D, text: string, x: number, y: number, z: number, width = 0.24): void {
  const canvas = document.createElement("canvas");
  canvas.width = 128;
  canvas.height = 32;
  const c = canvas.getContext("2d");
  if (c) {
    c.fillStyle = "#2b2d42";
    c.fillRect(0, 0, 128, 32);
    c.fillStyle = "#f8f4e8";
    c.font = "bold 18px sans-serif";
    c.textAlign = "center";
    c.fillText(text, 64, 23);
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  const m = new THREE.Mesh(new THREE.PlaneGeometry(width, width / 4), new THREE.MeshBasicMaterial({ map: tex }));
  m.position.set(x, y, z);
  parent.add(m);
}

/**
 * The food truck lot out of the service window: asphalt with painted bays,
 * the other trucks across the lot (tacos, pizza, ice cream) with their
 * awnings, picnic tables under umbrellas, string lights on poles, a few
 * trees, and the town's low rooftops under a morning sky.
 */
export function buildLot(front: number): Outside {
  const g = new THREE.Group();
  const sky = new THREE.Mesh(new THREE.SphereGeometry(VIEW_FAR - 20, 24, 12), new THREE.MeshBasicMaterial({ map: skyGradient(), side: THREE.BackSide, fog: false }));
  sky.position.set(2.6, 0, front);
  g.add(sky);

  const ground = new THREE.Mesh(new THREE.PlaneGeometry(400, 400), flat(0x55585c));
  ground.rotation.x = -Math.PI / 2;
  ground.position.set(2.6, -0.02, front + 190);
  g.add(ground);
  const paintLine = flat(0xe9e5d6);
  for (let i = -4; i <= 6; i++) span(i * 2.8 - 0.05, i * 2.8 + 0.05, -0.015, -0.01, front + 13, front + 18, paintLine, g);

  // The other trucks, facing us across the lot.
  const trucks: [number, number, number, number][] = [
    [-3.5, front + 8, 0xf2c14e, 0xd8392b], // tacos: yellow, red awning
    [3.2, front + 9, 0xd8392b, 0x2b2d42], // pizza: red
    [9.5, front + 8.5, 0xf5b8c8, 0x7fc4e8], // ice cream: pink, sky blue
  ];
  for (const [tx, tz, body, awning] of trucks) {
    span(tx - 2.2, tx + 2.2, 0.5, 2.9, tz, tz + 2.2, flat(body), g);
    span(tx - 2.2, tx - 1.0, 0.5, 2.2, tz - 0.9, tz, flat(body), g); // cab
    span(tx - 2.1, tx - 1.1, 1.4, 2.0, tz - 0.92, tz - 0.88, flat(0x2a3a48), g); // windscreen
    span(tx - 1.0, tx + 1.6, 1.25, 2.15, tz - 0.02, tz, flat(0x2a2a2e), g); // serving window
    const awn = new THREE.Mesh(new THREE.BoxGeometry(3.0, 0.04, 0.9), flat(awning));
    awn.position.set(tx + 0.3, 2.45, tz - 0.4);
    awn.rotation.x = 0.35;
    g.add(awn);
    for (const wx of [tx - 1.6, tx + 1.4]) {
      const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.42, 0.3, 14), flat(0x1d1d1f));
      wheel.rotation.x = Math.PI / 2;
      wheel.position.set(wx, 0.42, tz - 0.05);
      g.add(wheel);
    }
  }

  // Picnic tables under umbrellas between us and them.
  const wood = flat(0x9a6a3c);
  for (const [px, pz, umb] of [
    [0.6, front + 4.2, 0x2f6f6a],
    [5.2, front + 4.8, 0xd8392b],
    [-2.6, front + 4.6, 0xf2c14e],
  ] as const) {
    span(px - 0.9, px + 0.9, 0.72, 0.76, pz - 0.4, pz + 0.4, wood, g);
    for (const dz of [-0.62, 0.62]) span(px - 0.9, px + 0.9, 0.42, 0.46, pz + dz - 0.13, pz + dz + 0.13, wood, g);
    for (const dx of [-0.7, 0.7]) span(px + dx - 0.04, px + dx + 0.04, 0, 0.72, pz - 0.05, pz + 0.05, wood, g);
    span(px - 0.02, px + 0.02, 0.76, 2.1, pz - 0.02, pz + 0.02, flat(0xeeeeee), g);
    const top = new THREE.Mesh(new THREE.ConeGeometry(1.2, 0.4, 10), flat(umb));
    top.position.set(px, 2.3, pz);
    g.add(top);
  }

  // String lights: poles at the corners, lines of bulbs between them.
  const poles = [
    new THREE.Vector3(-6, 0, front + 2),
    new THREE.Vector3(11, 0, front + 2.5),
    new THREE.Vector3(12, 0, front + 12),
    new THREE.Vector3(-7, 0, front + 12),
  ];
  for (const p of poles) span(p.x - 0.06, p.x + 0.06, 0, 4.2, p.z - 0.06, p.z + 0.06, flat(0x3a3e42), g);
  const bulbs: THREE.Vector3[] = [];
  for (let i = 0; i < poles.length; i++) {
    const a = poles[i]!;
    const b = poles[(i + 1) % poles.length]!;
    for (let k = 1; k < 14; k++) {
      const u = k / 14;
      const sag = Math.sin(u * Math.PI) * 0.8;
      bulbs.push(new THREE.Vector3(a.x + (b.x - a.x) * u, 4.1 - sag, a.z + (b.z - a.z) * u));
    }
  }
  const bulbMesh = new THREE.InstancedMesh(new THREE.SphereGeometry(0.07, 8, 6), new THREE.MeshBasicMaterial({ color: 0xffe9a8 }), bulbs.length);
  bulbs.forEach((p, i) => bulbMesh.setMatrixAt(i, new THREE.Matrix4().makeTranslation(p.x, p.y, p.z)));
  g.add(bulbMesh);

  // Trees, and the town's rooftops beyond.
  for (const [tx, tz, s] of [
    [-10, front + 16, 1.2],
    [15, front + 15, 1],
    [6, front + 20, 1.4],
  ] as const) {
    span(tx - 0.12, tx + 0.12, 0, 2.2 * s, tz - 0.12, tz + 0.12, flat(0x6b4a2e), g);
    const crown = new THREE.Mesh(new THREE.SphereGeometry(1.4 * s, 10, 8), flat(0x4f8f3a));
    crown.position.set(tx, 3 * s, tz);
    g.add(crown);
  }
  const roofs = [0xc9b8a0, 0xa9b3bd, 0xd8c5a8, 0x9aa5ae, 0xbfae95];
  for (let i = 0; i < 14; i++) {
    const w = 6 + ((i * 37) % 7);
    const h = 4 + ((i * 53) % 9);
    span(-40 + i * 6.5, -40 + i * 6.5 + w, 0, h, front + 45 + ((i * 29) % 12), front + 52 + ((i * 29) % 12), flat(roofs[i % roofs.length]!), g);
  }

  mergeStatic(g);
  const fog = new THREE.Fog(SKY, 50, VIEW_FAR - 40);
  return { group: g, fog, animate: () => {} };
}
